export interface WorkBufferCopySegment {
  /** 该 segment 覆盖的 compact active source / interval lookup 起点。 */
  sourceBase: number
  /** 该 segment 覆盖的 compact active target 偏移。 */
  targetOffset: number
  /**
   * segment 覆盖的 active texel 数量。
   *
   * 当启用 includeTrailingPadding 时，实际 draw 面积由 width * height 表示，
   * count 仍保留不含尾部 padding 的 active 数量。
   */
  count: number
  /** WorkBuffer 纹理宽度。 */
  textureWidth: number
  /** 当前 splat 在 WorkBuffer 中的绝对起始行。 */
  lineStart: number
  /** viewport/scissor 的绝对 x 坐标。 */
  x: number
  /** viewport/scissor 的绝对 y 坐标。 */
  y: number
  /** segment 宽度，单位 texel。 */
  width: number
  /** segment 高度，单位 texel。 */
  height: number
}

export interface BuildWorkBufferCopySegmentsOptions {
  activeSplats: number
  textureWidth: number
  lineStart: number
  targetOffset?: number
  sourceBase?: number
  /**
   * 将 compact active range 的完整行和尾行合并为一次矩形 draw。
   *
   * 仅对从 0 开始的完整 compact range 生效；尾部 padding 依赖 shader bounds discard。
   */
  includeTrailingPadding?: boolean
}

export function buildWorkBufferCopySegments(
  options: BuildWorkBufferCopySegmentsOptions
): WorkBufferCopySegment[] {
  const textureWidth = Math.floor(options.textureWidth)
  const lineStart = Math.floor(options.lineStart)
  let remaining = Math.floor(options.activeSplats)
  let targetOffset = Math.floor(options.targetOffset ?? 0)
  let sourceBase = Math.floor(options.sourceBase ?? targetOffset)

  if (textureWidth <= 0 || remaining <= 0 || lineStart < 0 || targetOffset < 0) {
    return []
  }

  if (options.includeTrailingPadding && targetOffset === 0 && sourceBase === 0) {
    const fullRows = Math.floor(remaining / textureWidth)
    const tail = remaining % textureWidth

    if (fullRows > 0 && tail > 0) {
      return [
        {
          sourceBase,
          targetOffset,
          count: remaining,
          textureWidth,
          lineStart,
          x: 0,
          y: lineStart,
          width: textureWidth,
          height: fullRows + 1,
        },
      ]
    }
  }

  const segments: WorkBufferCopySegment[] = []

  while (remaining > 0) {
    const x = targetOffset % textureWidth
    const localY = Math.floor(targetOffset / textureWidth)

    let count: number
    let width: number
    let height: number

    if (x === 0 && remaining >= textureWidth) {
      const fullRows = Math.floor(remaining / textureWidth)
      count = fullRows * textureWidth
      width = textureWidth
      height = fullRows
    } else {
      count = Math.min(remaining, textureWidth - x)
      width = count
      height = 1
    }

    segments.push({
      sourceBase,
      targetOffset,
      count,
      textureWidth,
      lineStart,
      x,
      y: lineStart + localY,
      width,
      height,
    })

    remaining -= count
    targetOffset += count
    sourceBase += count
  }

  return segments
}

export function getWorkBufferCopyArea(segments: readonly WorkBufferCopySegment[]): number {
  let area = 0

  for (const segment of segments) {
    area += segment.width * segment.height
  }

  return area
}
